import { supabase } from '@/integrations/supabase/client';

export interface MintResult {
  ok: boolean;
  amount?: number;
  new_balance?: number;
  already_minted?: boolean;
  transaction_id?: string;
  reason?: 'cooldown' | 'daily_cap' | 'unknown_action' | string;
  retry_after_seconds?: number;
  error?: string;
}

export interface SpendResult {
  ok: boolean;
  used_subscription?: boolean;
  charged?: number;
  new_balance?: number | null;
  already_charged?: boolean;
  reason?: 'insufficient_funds';
  balance?: number;
  price?: number;
  error?: string;
}

export interface RedeemXpResult {
  ok: boolean;
  xp_redeemed?: number;
  coins_minted?: number;
  new_balance?: number;
  reason?: 'insufficient_xp';
  available_xp?: number;
  requested?: number;
  error?: string;
}

export const coinsService = {
  /** Mint coins for an action. Idempotent on (source, ref_id). */
  async mint(args: {
    source: string;
    ref_id?: string;
    amount?: number;
    action_key?: string;
    metadata?: Record<string, unknown>;
  }): Promise<MintResult> {
    const { data, error } = await supabase.functions.invoke('coins-mint', { body: args });
    if (error) {
      // Cooldown / cap responses come back as 429 with a JSON body that
      // supabase-js wraps into an error. Surface the body instead of throwing.
      const ctx: any = (error as any).context;
      const raw = ctx?.body;
      if (typeof raw === 'string') {
        try {
          return JSON.parse(raw) as MintResult;
        } catch {
          /* fall through */
        }
      }
      if (ctx && typeof ctx.text === 'function') {
        try {
          return JSON.parse(await ctx.text()) as MintResult;
        } catch {
          /* fall through */
        }
      }
      return { ok: false, error: error.message };
    }
    return data as MintResult;
  },

  /** Spend coins on a feature. Pro users pass through without charge. */
  async spend(feature: string, ref_id?: string): Promise<SpendResult> {
    const { data, error } = await supabase.functions.invoke('coins-spend', {
      body: { feature, ref_id },
    });
    if (error) {
      // 402 insufficient funds is returned with body, but supabase-js wraps as error.
      // Try to surface the body.
      const ctx: any = (error as any).context;
      if (ctx?.body) {
        try {
          const parsed = JSON.parse(ctx.body);
          return parsed as SpendResult;
        } catch {
          /* fall through */
        }
      }
      return { ok: false, error: error.message };
    }
    return data as SpendResult;
  },

  /** Convert XP into Coins at the configured ratio. */
  async redeemXp(xp_amount: number): Promise<RedeemXpResult> {
    const { data, error } = await supabase.functions.invoke('coins-redeem-xp', {
      body: { xp_amount },
    });
    if (error) return { ok: false, error: error.message };
    return data as RedeemXpResult;
  },

  /** Start a Stripe checkout for a coin pack. Opens in a new tab. */
  async startTopup(pack_id: string): Promise<{ url?: string; error?: string }> {
    const { data, error } = await supabase.functions.invoke('coins-topup', {
      body: { pack_id },
    });
    if (error) return { error: error.message };
    return data as { url: string };
  },
};